import React, { useEffect, useState } from 'react';
import { FaComments, FaEnvelope, FaFacebookF, FaLinkedinIn } from 'react-icons/fa';
import styled from 'styled-components';

import { SITE_CONFIG } from '../config/site';

const MENU_STORAGE_KEY = 'contact-menu-dismissed';

const Wrapper = styled.div`
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
`;

const MenuCard = styled.div`
  position: relative;
  width: 280px;
  border-radius: 1rem;
  border: 1px solid rgba(34, 211, 238, 0.3);
  background-color: #050816;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.65);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 0.75rem 1rem;
  background: linear-gradient(90deg, #0891b2, #0284c7);
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  text-align: start;
`;

const HeaderSubtitle = styled.p`
  margin: 0.2rem 0 0;
  font-size: 0.85rem;
  color: #e0f2fe;
  text-align: start;
`;

const Options = styled.div`
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OptionBase = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 0.9rem;
  border: 1px solid #4b5563;
  background-color: rgba(31, 41, 55, 0.7);
  text-align: start;
  color: #e5e7eb;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    transform 0.1s ease;

  &:hover {
    border-color: rgba(34, 211, 238, 0.5);
    background-color: rgba(31, 41, 55, 0.95);
    transform: translateY(-1px);
  }
`;

const OptionLink = styled.a`
  text-decoration: none;
  color: inherit;
`;

const IconCircle = styled.div<{ bg: string }>`
  height: 2.25rem;
  width: 2.25rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background-color: ${({ bg }) => bg};
  color: #ffffff;
`;

const OptionTitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
`;

const OptionSubtitle = styled.p`
  margin: 0.1rem 0 0;
  font-size: 0.75rem;
  color: #9ca3af;
`;

const ArrowOuter = styled.span`
  position: absolute;
  bottom: -0.8rem;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid rgba(34, 211, 238, 0.3);
`;

const ArrowInner = styled.span`
  position: absolute;
  bottom: -0.6rem;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #050816;
`;

const FabButton = styled.button`
  height: 3.5rem;
  width: 3.5rem;
  border: none;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: radial-gradient(circle at 0 0, #22d3ee, #0ea5e9);
  color: #ffffff;
  box-shadow: 0 0 18px rgba(34, 211, 238, 0.45);
  transition:
    transform 0.1s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.06);
    box-shadow: 0 0 26px rgba(34, 211, 238, 0.7);
  }
`;

/** Official WhatsApp logo SVG (adapted from your snippet) */
export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export interface ContactMenuCTAProps {
  onScrollToSection?: (sectionId: string) => void;
}

const ContactMenuCTA: React.FC<ContactMenuCTAProps> = ({ onScrollToSection }) => {
  const CommentsIcon = FaComments as unknown as React.ComponentType<{ size?: number }>;
  const EnvelopeIcon = FaEnvelope as unknown as React.ComponentType<{ size?: number }>;
  const LinkedInIcon = FaLinkedinIn as unknown as React.ComponentType<{ size?: number }>;
  const FacebookIcon = FaFacebookF as unknown as React.ComponentType<{ size?: number }>;

  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(MENU_STORAGE_KEY);
      setShowMenu(dismissed !== 'true');
    } catch {
      setShowMenu(true);
    }
  }, []);

  const handleClose = () => {
    setShowMenu(false);
    try {
      window.localStorage.setItem(MENU_STORAGE_KEY, 'true');
    } catch {
      // ignore
    }
  };

  const handleContactFormClick = () => {
    handleClose();
    onScrollToSection?.('contact');
  };

  const handleFabClick = () => {
    setShowMenu((prev) => {
      const next = !prev;
      if (!next) {
        try {
          window.localStorage.setItem(MENU_STORAGE_KEY, 'true');
        } catch {
          // ignore
        }
      }
      return next;
    });
  };

  return (
    <Wrapper>
      {showMenu && (
        <MenuCard role="dialog" aria-label="Let's Connect">
          <Header>
            <HeaderTitle>Let's Connect</HeaderTitle>
            <HeaderSubtitle>How would you like to reach me?</HeaderSubtitle>
          </Header>

          <Options>
            <OptionLink
              href={SITE_CONFIG.social.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OptionBase as="div">
                <IconCircle bg="#25D366">
                  <WhatsAppIcon />
                </IconCircle>
                <div>
                  <OptionTitle>Chat on WhatsApp</OptionTitle>
                  <OptionSubtitle>Speak with me now</OptionSubtitle>
                </div>
              </OptionBase>
            </OptionLink>

            <OptionBase as="button" type="button" onClick={handleContactFormClick}>
              <IconCircle bg="rgba(34,211,238,0.25)">
                <EnvelopeIcon size={18} />
              </IconCircle>
              <div>
                <OptionTitle>Send a message</OptionTitle>
                <OptionSubtitle>Fill out the form</OptionSubtitle>
              </div>
            </OptionBase>

            <OptionLink
              href={SITE_CONFIG.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OptionBase as="div">
                <IconCircle bg="rgba(56,189,248,0.25)">
                  <LinkedInIcon size={18} />
                </IconCircle>
                <div>
                  <OptionTitle>LinkedIn</OptionTitle>
                  <OptionSubtitle>Connect professionally</OptionSubtitle>
                </div>
              </OptionBase>
            </OptionLink>

            <OptionLink
              href={SITE_CONFIG.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OptionBase as="div">
                <IconCircle bg="rgba(59,130,246,0.3)">
                  <FacebookIcon size={18} />
                </IconCircle>
                <div>
                  <OptionTitle>Facebook</OptionTitle>
                  <OptionSubtitle>Follow and message</OptionSubtitle>
                </div>
              </OptionBase>
            </OptionLink>
          </Options>

          <ArrowOuter aria-hidden />
          <ArrowInner aria-hidden />
        </MenuCard>
      )}

      <FabButton
        type="button"
        onClick={handleFabClick}
        aria-label="Let's Connect"
        aria-expanded={showMenu}
        aria-haspopup="true"
      >
        <CommentsIcon size={22} />
      </FabButton>
    </Wrapper>
  );
};

export default ContactMenuCTA;
